import { useState, useEffect } from 'react';
import { LuBell, LuCheckCheck } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import NotificationItem from '../components/Notifications/NotificationItem';
import { useTranslation } from 'react-i18next';

export default function Notifications() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('unread'); // Set 'unread' as default
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, filter]);

  const fetchNotifications = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const filters = [{ name: 'user_id', op: 'Equal', value: Number(user.id) }];
      if (filter === 'unread') {
        filters.push({ name: 'is_read', op: 'Equal', value: false });
      }

      const { data, error } = await window.ezsite.apis.tablePage(70655, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: filters
      });

      if (error) {
        console.error('Failed to fetch notifications:', error);
        throw new Error(error);
      }
      setNotifications(data?.List || []);
    } catch (error) {
      console.error('Notifications fetch error:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.loadFailed'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        const { error } = await window.ezsite.apis.tableUpdate(70655, {
          id: Number(notification.id),
          is_read: true
        });
        if (error) {
          console.error('Failed to mark notification as read:', error);
        }
        fetchNotifications();
      }

      if (notification.link) {
        navigate(notification.link);
      }
    } catch (error) {
      console.error('Failed to handle notification:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.processFailed'),
        variant: 'destructive'
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = filter === 'unread' ? notifications : notifications.filter((n) => !n.is_read);

      if (unreadNotifications.length === 0) return;

      // Update in parallel for better performance
      await Promise.all(
        unreadNotifications.map((notification) =>
          window.ezsite.apis.tableUpdate(70655, {
            id: Number(notification.id),
            is_read: true
          })
        )
      );

      toast({
        title: t('common.success'),
        description: t('notifications.markReadSuccess')
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.markReadFailed'),
        variant: 'destructive'
      });
    }
  };

  const unreadCount = filter === 'unread' ? notifications.length : notifications.filter((n) => !n.is_read).length;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <LuBell className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('notifications.title')}</h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-600">
                    {t('notifications.unreadCount', { count: unreadCount })}
                  </p>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <LuCheckCheck className="w-4 h-4" />
                <span>{t('notifications.markAllRead')}</span>
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t('notifications.all')}
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'unread'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t('notifications.unread')}
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div>
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('notifications.loading')}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <LuBell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">
                {filter === 'unread'
                  ? t('notifications.noUnread')
                  : t('notifications.noNotifications')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}